import type { SelectProps, Option } from "@hightouch/formkit";
import type { ControllerRenderer, SharedProps } from ".";
import { useEffect, useState } from "react";
import { uniqBy } from "lodash";
import {
  BadgeInput,
  Box,
  Button,
  Combobox,
  GroupedCombobox,
  MultiSelect,
  RefreshIcon,
  Select,
} from "@hightouch/htui";
import { Controller, useFormContext } from "react-hook-form";
import { VALUE_LABEL_FUNCTION_MAP } from "src/formkit/constants";
import { useFormkitContext } from "src/formkit/components/formkit-context";

// Subtypes of the Select component
enum SelectType {
  Select,
  Combobox,
  GroupedCombobox,
  MultiSelect,
  BadgeInput,
}

export const FKSelect = ({
  isSetup,
  creatable,
  default: defaultValue,
  grouped,
  createLabelPrefix = "object",
  error,
  multi,
  name,
  options,
  placeholder,
  searchable,
  valueLabelFunctionKey,
  width,
  query,
  isDisabled,
}: SelectProps & { createLabelPrefix?: string } & SharedProps) => {
  const [customOptions, setCustomOptions] = useState<
    Array<{ label: string; value: string }>
  >([]);
  const [inputValue, setInputValue] = useState<string>("");
  const { getValues } = useFormContext();
  const { getUnavailableOptionReason } = useFormkitContext();
  // A string disables the option and shows as its reason.
  const isOptionUnavailable = getUnavailableOptionReason
    ? (option: { value: unknown }) =>
        (typeof option.value === "string" &&
          getUnavailableOptionReason(name, option.value, getValues())) ||
        false
    : undefined;

  if (searchable && options?.variables?.input?.variables) {
    options.variables.input.variables = {
      ...options.variables.input.variables,
      inputValue,
    };
  }

  // TODO(eric.lovett): This `biome-ignore` was part of a blanket suppression so that we could
  // turn on the rule. It is probably covering up a real bug. Please fix this or replace this
  // TODO with an explanation of why we don't want all deps listed.
  // biome-ignore lint/correctness/useExhaustiveDependencies: legacy suppression, probably a bug
  useEffect(() => {
    if (query.enabled && query.refetch && inputValue && inputValue !== "") {
      query.refetch();
    }
  }, [inputValue]);

  // Destinations like `sfmc` can return objects without `label` or `value`, so they need to be removed
  const nonEmptyDynamicOptions = (query.data ?? []).filter((option) => {
    return "label" in option && "value" in option;
  });

  const staticOrDynamicOptions = query.enabled
    ? nonEmptyDynamicOptions
    : uniqBy(options as Option[], "value");

  const combinedOptions = [...staticOrDynamicOptions, ...customOptions];
  const isCreatableOrQuery = creatable || query.enabled;

  // Grouped options are only used by `salesforce` sync form, and
  // it's configured to allow single selection and disable creatable options.
  // Until more destinations use grouped options, it's not necessary to
  // implement multi selection and creatable options for grouped options
  const type: SelectType = grouped
    ? SelectType.GroupedCombobox
    : multi
      ? isCreatableOrQuery
        ? SelectType.BadgeInput
        : SelectType.MultiSelect
      : isCreatableOrQuery
        ? SelectType.Combobox
        : SelectType.Select;
  const sharedProps = {
    isDisabled,
    isInvalid: Boolean(error),
    isLoading: query.enabled ? query.isFetching : false,
    placeholder,
    width,
    valueLabel:
      valueLabelFunctionKey && VALUE_LABEL_FUNCTION_MAP[valueLabelFunctionKey],
  };

  const sharedCreatableProps = {
    supportsCreatableOptions: creatable || searchable,
    createOptionMessage: (inputValue: string) => {
      return searchable
        ? `Search for "${inputValue}"`
        : `Create ${createLabelPrefix} "${inputValue}"`;
    },
  };
  const renderer: Record<SelectType, ControllerRenderer> = {
    [SelectType.Select]: ({ field }) => (
      <Select
        {...sharedProps}
        isClearable={true}
        isOptionDisabled={isOptionUnavailable}
        options={combinedOptions}
        value={field.value ?? defaultValue ?? undefined}
        onChange={(newValue) => {
          field.onChange(newValue ?? defaultValue ?? null);
        }}
      />
    ),
    [SelectType.Combobox]: ({ field }) => {
      // BUG: This violates the rule of hooks. Not updating until separate PR.
      // TODO(eric.lovett): This `biome-ignore` was part of a blanket suppression so that we could
      // turn on the rule. It is probably covering up a real bug. Please fix this or replace this
      // TODO with an explanation of why we don't want all deps listed.
      // biome-ignore lint/correctness/useExhaustiveDependencies: legacy suppression, probably a bug
      // biome-ignore lint/correctness/useHookAtTopLevel: legacy suppression
      useEffect(() => {
        if (searchable && inputValue && !query.isFetching) {
          if (query.data?.length) {
            const firstLookup = query.data[0].value;
            // If async lookups and search returned a value, set field to trigger modifiers
            if (!field.value) {
              field.onChange(firstLookup);
            }
          } else {
            setInputValue("");
          }
        }
      }, [query.data]);
      return (
        <Combobox
          {...sharedProps}
          {...sharedCreatableProps}
          isClearable={true}
          isOptionDisabled={isOptionUnavailable}
          options={combinedOptions}
          value={field.value ?? defaultValue ?? undefined}
          onChange={(newValue) => {
            setInputValue("");
            field.onChange(newValue ?? null);
          }}
          onCreateOption={async (inputValue) => {
            if (searchable) {
              setInputValue(inputValue);
              return;
            }
            const newOption = {
              label: inputValue,
              value: inputValue,
            };

            setCustomOptions((previousCustomOptions) => {
              return [...previousCustomOptions, newOption];
            });
            field.onChange(inputValue);
          }}
        />
      );
    },
    [SelectType.GroupedCombobox]: ({ field }) => (
      <GroupedCombobox
        {...sharedProps}
        optionGroups={query.data ?? []}
        value={field.value ?? defaultValue ?? undefined}
        onChange={(newValue) => {
          field.onChange(newValue ?? null);
        }}
      />
    ),
    [SelectType.MultiSelect]: ({ field }) => (
      <Box flex="1" minWidth={0}>
        <MultiSelect
          {...sharedProps}
          isClearable={true}
          options={combinedOptions}
          value={field.value ?? defaultValue ?? []}
          width="100%"
          onChange={(newValue) => {
            field.onChange(newValue ?? []);
          }}
        />
      </Box>
    ),
    [SelectType.BadgeInput]: ({ field }) => (
      <Box flex="1" minWidth={0}>
        <BadgeInput
          {...sharedProps}
          {...sharedCreatableProps}
          options={combinedOptions}
          value={field.value ?? defaultValue ?? []}
          width="100%"
          onChange={(newValue) => {
            field.onChange(newValue ?? []);
          }}
          onCreateOption={async (inputValue) => {
            const newOption = {
              label: inputValue,
              value: inputValue,
            };

            setCustomOptions((previousCustomOptions) => {
              return [...previousCustomOptions, newOption];
            });

            field.onChange([...(field.value ?? []), inputValue]);
          }}
        />
      </Box>
    ),
  };

  return (
    <Controller
      name={name}
      render={({ field }) => {
        const hasValue = multi
          ? Array.isArray(field.value) && field.value.length > 0
          : Boolean(field.value);

        // When user creates a custom option while creating a new destination,
        // this option must be added to a list of static/dynamic options on edit page,
        // otherwise selected option won't be displayed in a combobox
        if (!isSetup && creatable && hasValue) {
          const values = multi ? field.value : [field.value];

          for (const value of values) {
            const selectedOptionExists = combinedOptions.some((option) => {
              return option.value === value;
            });

            if (!selectedOptionExists) {
              combinedOptions.push({
                label: value,
                value,
              });
            }
          }
        }

        return (
          <Box alignItems="center" display="flex" gap={3}>
            {renderer[type]({ field })}
            {query.refetch && !searchable && (
              <Button
                icon={RefreshIcon}
                onClick={query.refetch}
                isDisabled={isDisabled}
              >
                Refresh
              </Button>
            )}
          </Box>
        );
      }}
    />
  );
};
